import { useEffect, useRef } from 'react';
import { ProgressBar } from '../components/ProgressBar.jsx';
import { SiteNote } from '../components/SiteNote.jsx';
import { StatNumber } from '../components/StatNumber.jsx';
import { portfolioContent } from '../content/portfolioContent.js';
import { BEAT_COUNT, DWELL, journeyPose } from '../scene/journeyPath.js';
import { getLenis, getScrollSnapshot } from '../scroll/scrollStore.js';

const { hero, who, work, experience, contact, footer } = portfolioContent;

const BEAT_LABELS = ['Arrival', 'About', 'Case 01', 'Case 02', 'Experience', 'Credentials', 'Proof', 'Contact'];

// Window helper: fade/scale a tile in during its beat's dwell, hold, recede.
function tileWindow(pose, beat, { inStart = 0.1, inEnd = 0.38, outStart = 0.86 } = {}) {
  if (pose.beat !== beat) return { k: pose.beat > beat ? 1 : 0, o: 0 };
  if (pose.phase === 'warp') return { k: 1, o: 0 };
  const t = pose.t;
  if (t < inStart) return { k: 0, o: 0 };
  if (t < inEnd) {
    const k = (t - inStart) / (inEnd - inStart);
    return { k, o: k };
  }
  if (t < outStart) return { k: 1, o: 1 };
  const k = (t - outStart) / (1 - outStart);
  return { k: 1 + k, o: 1 - k };
}

const smooth = (v) => {
  const x = Math.min(1, Math.max(0, v));
  return x * x * (3 - 2 * x);
};

export function JourneyMode({ onOpenCase }) {
  const refs = {
    heroTile: useRef(null),
    about: useRef(null),
    case1: useRef(null),
    dualLeft: useRef(null),
    dualRight: useRef(null),
    exp: useRef(null),
    contact: useRef(null),
    flash: useRef(null),
    hud: useRef(null),
    hint: useRef(null),
  };

  useEffect(() => {
    let raf;
    function frame() {
      const { progress } = getScrollSnapshot();
      const pose = journeyPose(progress);
      const set = (ref, style) => {
        if (ref.current) Object.assign(ref.current.style, style);
      };

      // 0 · hero — visible through the first dwell, fades as the push begins
      {
        const o = pose.beat === 0 && pose.phase === 'dwell' ? 1 - smooth((pose.t - 0.55) / 0.45) : 0;
        set(refs.heroTile, { opacity: o.toFixed(3), transform: `translate(-50%, -50%) translateY(${(1 - o) * -30}px)` });
      }
      // 1 · about — approaches to full view alongside the planet, then recedes
      {
        const { k, o } = tileWindow(pose, 1);
        const scale = k <= 1 ? 0.72 + 0.28 * smooth(k) : 1 + (k - 1) * 0.16;
        set(refs.about, {
          opacity: o.toFixed(3),
          transform: `translate(-50%, -50%) translateX(-21vw) scale(${scale.toFixed(3)})`,
        });
      }
      // 2 · case 1 — rides the orbit
      {
        const { k, o } = tileWindow(pose, 2);
        const scale = k <= 1 ? 0.76 + 0.24 * smooth(k) : 1 + (k - 1) * 0.14;
        set(refs.case1, {
          opacity: o.toFixed(3),
          transform: `translate(-50%, -50%) translateX(21vw) scale(${scale.toFixed(3)})`,
        });
      }
      // 3 · case 2 dual — in from the sides, meet, spin inward, part as we pass through
      {
        let xL = -58;
        let xR = 58;
        let rot = 0;
        let o = 0;
        let scale = 1;
        if (pose.beat === 3 && pose.phase === 'dwell') {
          const t = pose.t;
          if (t < 0.4) {
            const k = smooth(t / 0.4);
            xL = -58 + 49 * k;
            xR = 58 - 49 * k;
            o = Math.min(1, k * 1.6);
          } else if (t < 0.64) {
            const k = smooth((t - 0.4) / 0.24);
            xL = -9;
            xR = 9;
            rot = 74 * k;
            o = 1;
          } else {
            const k = smooth((t - 0.64) / 0.36);
            xL = -9 - 53 * k;
            xR = 9 + 53 * k;
            rot = 74;
            scale = 1 + k * 0.42;
            o = 1 - k * 0.75;
          }
        } else if (pose.beat > 3) {
          o = 0;
        }
        set(refs.dualLeft, {
          opacity: o.toFixed(3),
          transform: `translate(-50%, -50%) translateX(${xL}vw) perspective(1100px) rotateY(${rot}deg) scale(${scale.toFixed(3)})`,
        });
        set(refs.dualRight, {
          opacity: o.toFixed(3),
          transform: `translate(-50%, -50%) translateX(${xR}vw) perspective(1100px) rotateY(${-rot}deg) scale(${scale.toFixed(3)})`,
        });
      }
      // 4 · experience
      {
        const { k, o } = tileWindow(pose, 4);
        const scale = k <= 1 ? 0.76 + 0.24 * smooth(k) : 1 + (k - 1) * 0.14;
        set(refs.exp, {
          opacity: o.toFixed(3),
          transform: `translate(-50%, -50%) translateX(-20vw) scale(${scale.toFixed(3)})`,
        });
      }
      // 7 · contact — fades in over the receding solar system
      {
        const o = pose.beat === 7 ? smooth((pose.t - 0.22) / 0.33) : 0;
        set(refs.contact, {
          opacity: o.toFixed(3),
          transform: `translate(-50%, -50%) translateY(${(1 - o) * 34}px)`,
        });
      }
      // warp flash + HUD
      set(refs.flash, { opacity: (Math.pow(pose.warp, 1.6) * 0.32).toFixed(3) });
      if (refs.hud.current) {
        const links = refs.hud.current.children;
        for (let i = 0; i < links.length; i++) links[i].classList.toggle('active', i === pose.beat);
      }
      if (refs.hint.current) {
        // visibility, not opacity — the pulse keyframes own opacity
        refs.hint.current.style.visibility = progress < 0.02 ? '' : 'hidden';
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function jumpToBeat(index) {
    const lenis = getLenis();
    const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const target = ((index + DWELL * 0.55) / BEAT_COUNT) * max;
    if (lenis) lenis.scrollTo(target);
    else window.scrollTo({ top: target, behavior: 'smooth' });
  }

  return (
    <div className="journey-shell">
      <ProgressBar />
      <div className="journey-spacer" style={{ height: `${BEAT_COUNT * 170}vh` }} aria-hidden="true" />
      <div className="warp-flash" ref={refs.flash} aria-hidden="true" />

      {/* 0 · hero */}
      <div className="journey-tile journey-hero" ref={refs.heroTile}>
        <h1>{hero.name}</h1>
        <p>
          {hero.lineBefore}
          <span className="accent-text">{hero.lineAccent}</span>
        </p>
      </div>

      {/* 1 · about */}
      <div className="journey-tile rocking-panel" ref={refs.about}>
        <span className="eyebrow">{who.eyebrow}</span>
        {who.paragraphs.map((para, index) => (
          <p key={index}>
            {para.before}
            {para.stat && (
              <span className="inline-stat">
                {para.stat.target}
                {para.stat.suffix}
              </span>
            )}
            {para.after}
          </p>
        ))}
      </div>

      {/* 2 · case 1 */}
      <div className="journey-tile rocking-panel" ref={refs.case1}>
        <span className="eyebrow">Case 01 · {work.cases[0].tag}</span>
        <h2>{work.cases[0].title}</h2>
        <p className="journey-meta">{work.cases[0].meta}</p>
        <div className="case-stat">
          <StatNumber target={work.cases[0].stat.target} suffix={work.cases[0].stat.suffix} />
          <span className="stat-label">{work.cases[0].stat.label}</span>
        </div>
        <button type="button" className="breakdown-link" onClick={() => onOpenCase('solar')}>
          Read the case →
        </button>
      </div>

      {/* 3 · case 2 dual tiles */}
      <div className="journey-tile rocking-panel journey-dual" ref={refs.dualLeft}>
        <span className="eyebrow">Case 02 · {work.cases[1].tag}</span>
        <h2>{work.cases[1].title}</h2>
        <p className="journey-meta">{work.cases[1].meta}</p>
      </div>
      <div className="journey-tile rocking-panel journey-dual" ref={refs.dualRight}>
        <div className="case-stat">
          <StatNumber target={work.cases[1].stat.target} suffix={work.cases[1].stat.suffix} />
          <span className="stat-label">{work.cases[1].stat.label}</span>
        </div>
        {/* the Recon conversion sentence of the Context — slice indices track
            detail[0].p's sentence structure (". " also splits inside "U.S.",
            and the join re-knits it); re-derive them if that copy changes */}
        <p>{work.cases[1].detail[0].p.split('. ').slice(2, 6).join('. ')}.</p>
        <button type="button" className="breakdown-link" onClick={() => onOpenCase('copilot')}>
          Read the case →
        </button>
      </div>

      {/* 4 · experience */}
      <div className="journey-tile rocking-panel" ref={refs.exp}>
        <span className="eyebrow">{experience.eyebrow}</span>
        <h2>{experience.roleTitle}</h2>
        <p className="role-reframe">{experience.roleReframe}</p>
        <div className="journey-stats">
          {experience.stats.map((stat) => (
            <div key={stat.label} className="strip-stat">
              <StatNumber target={stat.target} suffix={stat.suffix} />
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
        <button type="button" className="breakdown-link" onClick={() => onOpenCase('experience')}>
          Read the detail →
        </button>
      </div>

      {/* 7 · contact */}
      <div className="journey-tile rocking-panel journey-contact" ref={refs.contact}>
        <span className="eyebrow">{contact.eyebrow}</span>
        <h2>{contact.title}</h2>
        <p>{contact.line}</p>
        <div className="cta-row">
          {contact.actions.map((action) => (
            <a
              key={action.label}
              href={action.href}
              className={`btn${action.primary ? ' btn-primary' : ' btn-secondary'}`}
              {...(action.external ? { target: '_blank', rel: 'noopener' } : {})}
            >
              {action.label}
            </a>
          ))}
          {contact.site && (
            <a className="btn btn-site" href={contact.site.href} target="_blank" rel="noopener">
              {contact.site.label} <span aria-hidden="true">↗</span>
            </a>
          )}
        </div>
        <SiteNote note={contact.site?.note} />
        <p className="footer-credit">{footer.credit}</p>
      </div>

      {/* HUD */}
      <nav className="journey-hud" ref={refs.hud} aria-label="Journey beats">
        {BEAT_LABELS.map((label, index) => (
          <a
            key={label}
            href={`#beat-${index}`}
            title={label}
            aria-label={label}
            onClick={(event) => {
              event.preventDefault();
              jumpToBeat(index);
            }}
          />
        ))}
      </nav>
      <div className="journey-hint" ref={refs.hint} aria-hidden="true">
        Scroll
      </div>
    </div>
  );
}
